// This file is used to map the fields for the NGL West Virginia Goods and Services Form
import { getNGLFieldNames } from '@/components/documents/Foxit/FormMapping/FieldNameProviders/NGLFieldNameProvider'
import { useCustomItemFields } from '@/components/documents/Foxit/FormMapping/composables/CustomItemFields'
import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import {
	type MappedCartItem,
	MultipleItemsMethod,
	useFormFillHelper,
} from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useIdentitySetup } from '@/components/documents/Foxit/FormMapping/composables/IdentityFields'
import { useSupplementalFields } from '@/components/documents/Foxit/FormMapping/composables/SupplementalFields'
import {
	groupedTotal,
	type GroupedTotalConfig,
	type SectionTotalConfig,
	useTotalFields,
} from '@/components/documents/Foxit/FormMapping/composables/TotalFields'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import type { CartItemAndLineItem } from '@/mixins/merchandise'
import type { CustomCartItem } from '@/types'

const itemDescription = (item: MappedCartItem) => {
	if (!item.shortDescription) {
		return item.name
	}

	if (!item.name) {
		return item.shortDescription
	}

	return `${item.name}: ${item.shortDescription}`
}

export default class NGLWVGoodsAndServicesForm {
	private fieldNameProvider = getNGLFieldNames()

	// Standard list of fields for this form.
	// These values point to field names in the fieldNameProvider, as well as functions to get the value for the field via the fieldValueProvider
	private fields: (keyof typeof this.fieldNameProvider)[] = [
		// Header
		'funeralHomeName',
		'funeralHomeFullAddress',
		'purchaserFullName',
		'purchaserFullAddress',
		'conditionalInsuredFields',
		'insuredFullName',

		// Service
		'basicServicesPrice',
		'embalmingAndRefrigerationPrice',
		'multilineBodyPreparationFields',
		'funeralMemorialServicePrice',
		'visitationValue',
		'visitationPrice',
		'facilityUseOtherGroupFields',
		'transferMileRadiusFields',
		'automotiveOtherFields',
		'packageIndicator',
		'singleLineOtherServicesFields',

		// // Merchandise
		'casketFields',
		'outerBurialFields',
		'urnFields',
		'merchandiseOtherAndDiscountMultilineFields',

		// Cash Advance
		'multilineCashAdvanceOtherFieldsWithDiscount',

		// Supplemental
		'packageInfoWithPrepend',
	]

	private unfilledFields = [
		'contractNumber',
		'gEmbalmingViewingCheck',
		'gEmbalmingViewingCheck',
		'gEmbalmingPersonalCheck',
		'gEmbalmingOtherCheck',
		'reasonForEmbalmingDesc1',
		'gOuterBurialCemeteryRequiredCheck',
		'gCrematoryAltContainerRequiredCheck',
	]
	private sectionTotals: SectionTotalConfig[] = [
		{ section: 'service' },
		{ section: 'merchandise' },
		{ section: 'cashAdvance' },
		{ section: 'otherServices' },
	]
	private groupedTotals: GroupedTotalConfig[] = [
		groupedTotal('guaranteed'),
		groupedTotal('funeral'),
	]

	// West Virginia requires these form descriptions to show both the item name and G&S description.
	// The shared mapper supplies the G&S description, so this updates only the affected WV PDF fields.
	private updateDescriptionFields(
		mainFields: FieldValueResult[],
		descriptionFields: Record<string, string>,
		cartItems: MappedCartItem[],
		formatItem: (item: MappedCartItem) => string = itemDescription
	) {
		let itemIndex = 0

		for (const pdfFieldName of Object.values(descriptionFields)) {
			if (!pdfFieldName.includes('Desc')) {
				continue
			}

			const cartItem = cartItems[itemIndex++]
			if (!cartItem) {
				break
			}

			const mappedField = mainFields.find(
				(field) => field.name === pdfFieldName
			)
			if (!mappedField || mappedField.value === 'See Supplemental Page') {
				continue
			}

			mappedField.value = formatItem(cartItem)
		}
	}

	// Setup prior to building form filling array.
	constructor(planHolderId: string, cartItems?: CartItemAndLineItem[]) {
		const { identitySetup } = useIdentitySetup()
		const {
			setMultipleItemsMethod,
			setFieldNameProvider,
			setCartItems,
			setIgnoreSupplementalTextList,
			setNonServiceCategories,
			setOtherLineCounts,
		} = useFormFillHelper()

		setOtherLineCounts({ cashAdvance: 3 })
		identitySetup(planHolderId, ['planHolder', 'funeralHome'])
		setIgnoreSupplementalTextList(
			new Set([
				'gCasketModelDesc',
				'gCasketExteriorDesc',
				'gCasketInteriorDesc',
				'gOuterBurialModelDesc',
				'gOuterBurialConstructedOfDesc',
				'gUrnModelDesc',
				'gUrnConstructedOfDesc',
			])
		)

		setMultipleItemsMethod(MultipleItemsMethod.Supplemental)
		setFieldNameProvider(this.fieldNameProvider)
		setNonServiceCategories()

		if (cartItems) {
			setCartItems(cartItems)
		}
	}

	// Build form filling array.
	getFormMappings() {
		const {
			formatFields,
			mapFields,
			findCartItems,
			findCartItemsInMultipleSubcategories,
		} = useFormFillHelper()
		const { getTotalsFields } = useTotalFields()
		const { getSupplementalFields } = useSupplementalFields()

		const mainFields = mapFields(this.fields)

		// Body Preparation
		this.updateDescriptionFields(
			mainFields,
			this.fieldNameProvider.multilineBodyPreparationFields as Record<
				string,
				string
			>,
			findCartItems(PbProductType.Service, PbSubcategory.ServiceBodyPreparation)
		)
		// Other Facilities
		this.updateDescriptionFields(
			mainFields,
			this.fieldNameProvider.facilityUseOtherGroupFields as Record<
				string,
				string
			>,
			findCartItems(PbProductType.Service, PbSubcategory.ServiceOtherFacilities)
		)
		// Automotive Other
		this.updateDescriptionFields(
			mainFields,
			this.fieldNameProvider.automotiveOtherFields as Record<string, string>,
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationAutomotiveOther
			)
		)
		// Merchandise Other; discounts continue to display their G&S description only.
		this.updateDescriptionFields(
			mainFields,
			this.fieldNameProvider
				.merchandiseOtherAndDiscountMultilineFields as Record<string, string>,
			findCartItemsInMultipleSubcategories(PbProductType.Merchandise, [
				PbSubcategory.Other,
				PbSubcategory.Discount,
			]),
			(item) =>
				item.subcategory === PbSubcategory.Other
					? itemDescription(item)
					: (item.shortDescription ?? '')
		)

		const supplementalFields = getSupplementalFields(mainFields)
		const totalFields = getTotalsFields(
			mainFields,
			this.sectionTotals,
			this.groupedTotals
		)

		return formatFields([...mainFields, ...supplementalFields, ...totalFields])
	}

	// Mappings for only the fields a custom item is filled into, so adding one
	// doesn't overwrite what the AFP entered by hand
	getCustomItemFormMappings(customItemId: CustomCartItem['id']) {
		const { getCustomItemFormMappings } = useCustomItemFields()

		return getCustomItemFormMappings(
			this.getFormMappings(),
			this.fields,
			customItemId
		)
	}
}
